import React, { useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, X, Search, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/auth';
import { useRestaurantImages } from '@/hooks/useRestaurantMedia';
import { MediaCard } from '@/components/media/MediaCard';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface MediaSelectorProps {
  value: string[]; // Array of selected image IDs
  onChange: (ids: string[]) => void;
  min?: number;
  max?: number;
  showRefresh?: boolean; // Show refresh button
}

/**
 * MediaSelector - Multi-select image picker for reference images
 *
 * Features:
 * - Grid display of available images
 * - Checkbox selection (click card to toggle)
 * - Search by filename and tags
 * - Filter by source type (all, uploaded, AI-generated)
 * - Min/max validation
 * - Selection count display
 * - Clear all button
 */
export const MediaSelector: React.FC<MediaSelectorProps> = ({
  value,
  onChange,
  min = 1,
  max = 10,
  showRefresh = false
}) => {
  const { user } = useAuth();
  const { images, isLoading, error, refetch } = useRestaurantImages(user!.organisationId);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'uploaded' | 'ai-generated'>('all');

  // Filter and search images
  const filteredImages = useMemo(() => {
    let filtered = images;

    // Filter by source type
    if (sourceFilter === 'uploaded') {
      filtered = filtered.filter(img => img.source_type === 'uploaded');
    } else if (sourceFilter === 'ai-generated') {
      filtered = filtered.filter(img => img.source_type !== 'uploaded');
    }

    // Search by filename or tags
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(img => {
        const filenameMatch = img.original_filename?.toLowerCase().includes(query);
        const tagsMatch = img.tags?.some(tag => tag.toLowerCase().includes(query));
        return filenameMatch || tagsMatch;
      });
    }

    return filtered;
  }, [images, searchQuery, sourceFilter]);

  const handleToggle = (imageId: string) => {
    if (value.includes(imageId)) {
      // Remove from selection
      onChange(value.filter(id => id !== imageId));
    } else if (value.length < max) {
      // Add to selection (if not at max)
      onChange([...value, imageId]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load images: {(error as Error).message}
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading images...</span>
      </div>
    );
  }

  const isAtMax = value.length >= max;
  const isBelowMin = value.length < min;
  const hasNoImages = images.length === 0;
  const hasNoResults = filteredImages.length === 0 && images.length > 0;

  if (hasNoImages) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <p className="text-muted-foreground">No images available</p>
        <p className="text-sm text-muted-foreground mt-1">
          Upload or generate images first to use them as references
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with selection count and action buttons */}
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <Label>
            Select Reference Images {value.length > 0 && `(${value.length} of ${max} selected)`}
          </Label>
          <p className="text-xs text-muted-foreground">
            Select {min === max ? min : `${min}-${max}`} image{max > 1 ? 's' : ''} to use as reference{max > 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex gap-2">
          {showRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1"
              title="Refresh image list"
            >
              <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
              {!isRefreshing && 'Refresh'}
            </Button>
          )}
          {value.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by filename or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sourceFilter} onValueChange={(val) => setSourceFilter(val as typeof sourceFilter)}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Images</SelectItem>
            <SelectItem value="uploaded">Uploaded Only</SelectItem>
            <SelectItem value="ai-generated">AI-Generated Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Validation messages */}
      {isAtMax && (
        <Alert>
          <AlertDescription>
            Maximum of {max} images selected. Deselect an image to choose a different one.
          </AlertDescription>
        </Alert>
      )}

      {isBelowMin && value.length > 0 && (
        <Alert>
          <AlertDescription>
            Select at least {min} image{min > 1 ? 's' : ''} to continue
          </AlertDescription>
        </Alert>
      )}

      {/* No results message */}
      {hasNoResults && (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">No images match your search</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your search or filter
          </p>
        </div>
      )}

      {/* Image Grid */}
      {!hasNoResults && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image) => {
            const isSelected = value.includes(image.id);
            const canSelect = isSelected || !isAtMax;

            return (
              <div
                key={image.id}
                className={`relative ${!canSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <MediaCard
                  media={image}
                  selected={isSelected}
                  onSelect={() => canSelect && handleToggle(image.id)}
                  showActions={false}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Results count */}
      {!hasNoResults && (
        <p className="text-xs text-muted-foreground text-center">
          Showing {filteredImages.length} of {images.length} image{images.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};

export default MediaSelector;
